/**
 * Dialogue data for cutscenes, Elder encounters, and level transitions.
 *
 * Each dialogue sequence is an array of { speaker, text, duration } entries
 * that map directly to CutsceneStep "dialogue" steps.
 *
 * Character voices:
 *   Minna  — observant, ethereal, gentle. Sees energy and hidden patterns.
 *   Basil  — practical, warm, mechanical. Thinks in gears and leverage.
 *   Elders — wise, unhurried, slightly cryptic. Speak in metaphor.
 */

export interface DialogueLine {
  speaker: string;
  text: string;
  duration: number;
}

// ─── Opening Cutscene (Liminal Atrium) ──────────────────────────

export const OPENING_CUTSCENE: DialogueLine[] = [
  {
    speaker: "Minna",
    text: "The dust remembers something, Basil. Can you feel it? Every mote is holding its breath.",
    duration: 4500,
  },
  {
    speaker: "Basil",
    text: "I feel a draught through the floorboards and a dozen seized hinges. This place hasn't moved in ages.",
    duration: 4500,
  },
  {
    speaker: "Minna",
    text: "There are threads of light under the wallpaper... faint, but alive. The Atrium wants to wake up.",
    duration: 4500,
  },
  {
    speaker: "Basil",
    text: "Then let's give it a reason to. You read the currents, I'll work the mechanisms. Together, like always.",
    duration: 4500,
  },
];

// ─── Level Transition Lines ─────────────────────────────────────

export const LEVEL_INTRO: Record<string, DialogueLine[]> = {
  LiminalAtrium: [
    {
      speaker: "",
      text: "The Liminal Atrium — a dusty Victorian sunroom, suspended between what was and what could be.",
      duration: 4000,
    },
  ],
  RedwoodScrapyard: [
    {
      speaker: "Minna",
      text: "The trees here are enormous... but something heavy is wrapped around them. Chains and rust.",
      duration: 4000,
    },
    {
      speaker: "Basil",
      text: "Motorcycle parts. Someone bolted an entire scrapyard to these poor redwoods. We'll sort it out.",
      duration: 4000,
    },
  ],
  OvergrownConservatory: [
    {
      speaker: "Basil",
      text: "This whole conservatory is running hot. Brass valves, wet glass, and roots pushing through the floor.",
      duration: 4000,
    },
    {
      speaker: "Minna",
      text: "The mist is hiding frightened little heartbeats. We need to regulate the room before anything else panics.",
      duration: 4000,
    },
  ],
  ClockworkCanopy: [
    {
      speaker: "Minna",
      text: "We're above the canopy now. The stars are so close... and the gears are everywhere, silent and waiting.",
      duration: 4500,
    },
    {
      speaker: "Basil",
      text: "An observatory built into the treetops. Brass and glass and starlight. Someone wanted to read the sky.",
      duration: 4500,
    },
    {
      speaker: "Minna",
      text: "The constellations are misaligned. The orrery has stopped turning. We need to bring it all back into harmony.",
      duration: 5000,
    },
  ],
};

// ─── Elder Kitty Encounter (Redwood Scrapyard) ──────────────────
//
// Elder Kitty is a rugged, battle-scarred tomcat: nicked ears, a slow
// drawl, dust ground into his fur. He teaches the "Wisdom of Grounding".
// At least one of his lines below is verbatim canon from the game-book.

export const ELDER_KITTY_ENCOUNTER: DialogueLine[] = [
  {
    speaker: "Elder Kitty",
    text: "So. Two little shadows come picking through my scrapyard. Mind the rust — it bites back.",
    duration: 5000,
  },
  {
    speaker: "Minna",
    text: "Elder Kitty... the Scrapyard is breathing again. We freed the crane, fed the crows, calmed the kittens.",
    duration: 4500,
  },
  {
    speaker: "Elder Kitty",
    text: "You cannot force a blocked gear. You must anchor yourself, and let the tension release naturally.",
    duration: 6000,
  },
  {
    speaker: "Basil",
    text: "Learned that the hard way. Braced up, held still, and the whole mess finally let go.",
    duration: 4500,
  },
  {
    speaker: "Elder Kitty",
    text: "Stillness before motion. Weight before will. That is the Wisdom of Grounding — and it has kept this old tom on his feet through worse than rust.",
    duration: 6000,
  },
  {
    speaker: "Elder Kitty",
    text: "Take this with you: the Resonant Drop. When the tinker anchors and the quiet one purrs, the ground remembers how to listen.",
    duration: 6000,
  },
];

// ─── Elder Gus Encounter (Overgrown Conservatory) ───────────────
//
// Canon: Elder Gus teaches "The Wisdom of the Still Point" per the
// game-book. His verbatim quote is "True mastery isn't keeping up
// with chaos. It is finding the quiet center of a frantic system,
// and pausing." The previous wording framed his lesson as "Sequence
// and regulation" which conflated the apex mechanic (solving in
// sequence) with the wisdom (stillness within chaos). Updated to
// match canon.

export const ELDER_GUS_ENCOUNTER: DialogueLine[] = [
  {
    speaker: "Elder Gus",
    text: "Splendid timing. The sourdough was about to over-prove. One must respect the stillness between beats.",
    duration: 5000,
  },
  {
    speaker: "Minna",
    text: "Elder Gus, we guided the opossums home through the mist. The foxes never saw us.",
    duration: 4000,
  },
  {
    speaker: "Elder Gus",
    text: "Because you learned to pause. The warren was frantic — and you found its quiet center.",
    duration: 5000,
  },
  {
    speaker: "Basil",
    text: "The regulator taught us. Rush the valves and the whole system chokes. Hold the room still, and it breathes.",
    duration: 4500,
  },
  {
    speaker: "Elder Gus",
    text: "True mastery isn't keeping up with chaos. It is finding the quiet center of a frantic system, and pausing.",
    duration: 6500,
  },
  {
    speaker: "Elder Gus",
    text: "That is the Wisdom of the Still Point. Take the Chronos Bubble with you — it bends time to your rhythm so you can always find that center again.",
    duration: 6500,
  },
];

// ─── Elder Leo Returns — Clockwork Canopy (Level 3) ────────────

export const ELDER_LEO_CANOPY_ENCOUNTER: DialogueLine[] = [
  {
    speaker: "Elder Leo",
    text: "You have climbed higher than I dared hope. The canopy remembers you both.",
    duration: 5000,
  },
  {
    speaker: "Minna",
    text: "Elder Leo! The orrery is turning again. The constellations are aligned.",
    duration: 4000,
  },
  {
    speaker: "Elder Leo",
    text: "You learned grounding in the Scrapyard. Sequence in the Burrow. And now, here among the stars — integration.",
    duration: 5500,
  },
  {
    speaker: "Basil",
    text: "Every gear, every lens, every shadow and pulse — they all needed each other. Just like us.",
    duration: 4500,
  },
  {
    speaker: "Elder Leo",
    text: "That is the final Wisdom. Harmony is not the absence of difference — it is difference working as one.",
    duration: 5500,
  },
  {
    speaker: "Elder Leo",
    text: "The Clockwork Canopy sings again. And it sings because two brave cats remembered how to listen together.",
    duration: 5500,
  },
  {
    speaker: "Minna",
    text: "Thank you, Elder Leo. We hear it now — all of it, humming in time.",
    duration: 4000,
  },
  {
    speaker: "Basil",
    text: "The gears, the stars, the heartbeats. One rhythm. One song.",
    duration: 4000,
  },
  // ── Canon closing stanza (verbatim from the game-book) ──
  {
    speaker: "Grand Elder Leo",
    text: "The machine was but a mirror, young ones.",
    duration: 4500,
  },
  {
    speaker: "Grand Elder Leo",
    text: "The world did not mend because you turned a gear,",
    duration: 4000,
  },
  {
    speaker: "Grand Elder Leo",
    text: "or because you caught a ghost.",
    duration: 3500,
  },
  {
    speaker: "Grand Elder Leo",
    text: "It mended because you finally moved as one.",
    duration: 5000,
  },
  {
    speaker: "Grand Elder Leo",
    text: "You have everything you need within you.",
    duration: 4500,
  },
  {
    speaker: "Grand Elder Leo",
    text: "And you always did, and you always will.",
    duration: 5000,
  },
];

// ─── Control Hints ──────────────────────────────────────────────

// ─── Banter Quips (contextual one-liners) ──────────────────────

export interface BanterQuip {
  speaker: "Minna" | "Basil";
  text: string;
}

export type BanterCategory =
  | "puzzleSolved"
  | "abilityMinna"
  // Per-ability Minna buckets. Same pattern as the Basil split:
  // BanterSystem uses these first and falls back to `abilityMinna`
  // when the specific pool is empty. Prevents mismatches like Aura
  // Sight firing "The shadows welcome me." (a Shadow Slip line).
  | "abilityMinnaAuraSight"
  | "abilityMinnaShadowSlip"
  | "abilityMinnaPurr"
  | "abilityBasil"
  // Per-ability Basil buckets. BanterSystem uses these first and falls
  // back to the generic `abilityBasil` pool when a specific one is
  // empty. Per-ability pools prevent mismatches like "Harness locked"
  // firing on Overclocked Leap.
  | "abilityBasilAnchor"
  | "abilityBasilLeap"
  | "abilityBasilTurnTable"
  | "abilityCoop"
  | "npcCalmed"
  | "idle"
  | "coopProximity"
  | "portalOpened"
  | "crystalCharged"
  | "cameraTetherClamped";

export const BANTER: Record<BanterCategory, BanterQuip[]> = {
  puzzleSolved: [
    { speaker: "Minna", text: "The resonance is restored." },
    { speaker: "Basil", text: "Gears aligned. Lovely." },
    { speaker: "Minna", text: "Another thread woven back in place." },
    { speaker: "Basil", text: "That mechanism purred like a kitten." },
    { speaker: "Minna", text: "The energy flows freely again." },
    { speaker: "Basil", text: "Right, what's next on the docket?" },
    { speaker: "Minna", text: "Did you feel that shift? Beautiful." },
    { speaker: "Basil", text: "Tidy work, if I say so myself." },
  ],
  abilityMinna: [
    // Generic Minna fallback — only fires when a per-ability pool is
    // missing or empty. Kept ability-agnostic so it never reads as a
    // specific ability's telegraph.
    { speaker: "Minna", text: "Attuned." },
    { speaker: "Minna", text: "Listening..." },
  ],
  abilityMinnaAuraSight: [
    { speaker: "Minna", text: "I see what's hidden..." },
    { speaker: "Minna", text: "The unseen world opens." },
    { speaker: "Minna", text: "There are patterns beneath patterns." },
    { speaker: "Minna", text: "The currents are visible now." },
  ],
  abilityMinnaShadowSlip: [
    { speaker: "Minna", text: "The shadows welcome me." },
    { speaker: "Minna", text: "Between the dark places." },
    { speaker: "Minna", text: "Unseen — at least for now." },
  ],
  abilityMinnaPurr: [
    { speaker: "Minna", text: "Shhh... let the calm spread." },
    { speaker: "Minna", text: "Breathe with me, little ones." },
    { speaker: "Minna", text: "The frantic can rest." },
    { speaker: "Minna", text: "Quiet in, quiet out." },
  ],
  abilityBasil: [
    { speaker: "Basil", text: "Harness active." },
    { speaker: "Basil", text: "Kinetic flow engaged." },
    { speaker: "Basil", text: "Give me a moment." },
  ],
  abilityBasilAnchor: [
    { speaker: "Basil", text: "Brace engaged!" },
    { speaker: "Basil", text: "Harness locked — I'm not budging." },
    { speaker: "Basil", text: "Pneumatics holding steady." },
    { speaker: "Basil", text: "I'm the winch now." },
  ],
  abilityBasilLeap: [
    { speaker: "Basil", text: "Overclock! Hold on, Minna!" },
    { speaker: "Basil", text: "Full thrust to the struts!" },
    { speaker: "Basil", text: "Wind it up — let it fly!" },
    { speaker: "Basil", text: "Kinetic burst on three!" },
  ],
  abilityBasilTurnTable: [
    { speaker: "Basil", text: "Let's give this dial a spin." },
    { speaker: "Basil", text: "Scratching the groove." },
    { speaker: "Basil", text: "Dexterity engaged — watch the timing." },
    { speaker: "Basil", text: "Paws on the record." },
  ],
  abilityCoop: [
    { speaker: "Basil", text: "Together now — on my mark!" },
    { speaker: "Minna", text: "Our rhythms align. Ready." },
    { speaker: "Basil", text: "Brace and purr — here we go!" },
    { speaker: "Minna", text: "Time bends to our will." },
  ],
  npcCalmed: [
    { speaker: "Minna", text: "There now, all is well." },
    { speaker: "Basil", text: "Easy does it, little one." },
    { speaker: "Minna", text: "Your heartbeat steadies." },
    { speaker: "Basil", text: "See? Nothing to fuss about." },
  ],
  idle: [
    { speaker: "Basil", text: "*taps harness impatiently*" },
    { speaker: "Minna", text: "The shadows are restless..." },
    { speaker: "Basil", text: "My gears are going cold here." },
    { speaker: "Minna", text: "I sense something waiting for us." },
    { speaker: "Basil", text: "*adjusts brass fitting absently*" },
    { speaker: "Minna", text: "Even the dust wants us to move." },
    { speaker: "Basil", text: "Shall we? These puzzles won't solve themselves." },
    { speaker: "Minna", text: "The threads are humming. We should go." },
    { speaker: "Basil", text: "*polishes a valve, whistles softly*" },
    { speaker: "Minna", text: "The air tastes of old clockwork." },
  ],
  coopProximity: [
    { speaker: "Minna", text: "Your warmth steadies me." },
    { speaker: "Basil", text: "Stick close, yeah?" },
    { speaker: "Minna", text: "Together, we see more clearly." },
    { speaker: "Basil", text: "Two paws better than one, always." },
    { speaker: "Minna", text: "I feel stronger near you." },
    { speaker: "Basil", text: "Right beside you, as ever." },
  ],
  portalOpened: [
    { speaker: "Minna", text: "The way forward opens." },
    { speaker: "Basil", text: "Portal's live — after you." },
    { speaker: "Minna", text: "A new thread beckons." },
    { speaker: "Basil", text: "Gate's clear. Let's roll." },
  ],
  crystalCharged: [
    { speaker: "Minna", text: "The crystal sings." },
    { speaker: "Basil", text: "Charged up and glowing bright." },
    { speaker: "Minna", text: "Its light reaches deep." },
    { speaker: "Basil", text: "That's got some juice in it now." },
  ],
  cameraTetherClamped: [
    { speaker: "Minna", text: "Slow down, Basil! The tether is pulling me!" },
    { speaker: "Basil", text: "Hold your horses, Minna! Let's stay together." },
    { speaker: "Minna", text: "We are drifting too far..." },
    { speaker: "Basil", text: "Don't wander off, I've got a heavy harness here!" },
    { speaker: "Minna", text: "Wait for me!" },
    { speaker: "Basil", text: "Keep in stride, side by side." },
  ],
};

export const CONTROL_HINTS = {
  SOLO: [
    "Solo — WASD to move  |  Space to jump  |  Tab to switch cats  |  F2 for two-player co-op  |  Esc to pause",
  ],
  COOP: [
    "Local co-op — Minna: WASD + Space + F. Basil: Arrows + Shift/Enter + comma.  |  F2 to play solo (Tab then swaps cats).",
  ],
  GUARDIAN_DOG_HINT:
    "A guardian dog dozes on the rug. The gramophone's lullaby soothes them — Basil can wind it while Minna slips past.",
} as const;

/**
 * Pick the movement-controls hint matching the live input mode.
 *
 * Levels used to hard-code `CONTROL_HINTS.SOLO[0]` in their intro
 * cutscenes, but the game boots in local co-op — where Tab (the hint's
 * headline instruction) does nothing and Basil is on the arrow keys.
 * A lone player following the on-screen text was stranded at the first
 * Basil interaction. Always derive the hint from `InputManager.soloMode`.
 */
export function controlHintForMode(solo: boolean): string {
  return solo ? CONTROL_HINTS.SOLO[0] : CONTROL_HINTS.COOP[0];
}
